import { AbstractService } from "./AbstractService";
import { ConfigurableInterface } from "./ConfigurableInterface";
import AWS from "aws-sdk";
import { AwsLanguage } from "../types";
import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
  LanguageCode
} from "@aws-sdk/client-transcribe-streaming";
import MicrophoneStream from "microphone-stream";
import { Buffer } from "buffer";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";

export interface AwsServiceInterface extends ConfigurableInterface {
  startTranscription(
    stream: MediaStream,
    sourceLanguage: AwsLanguage,
    callback: Function
  ): Promise<void>;

  stopTranscription(): Promise<void>;

  translate(
    text: string,
    sourceLanguage: AwsLanguage,
    targetLanguage: AwsLanguage
  ): Promise<{ audio?: string; text: string }>;

  getFile(path: string): Promise<Uint8Array>;
}

export class AwsService extends AbstractService implements AwsServiceInterface {
  private isInitialized = false;

  private services: Record<
    "s3" | "polly" | "translate" | "transcribe",
    any | null
  > = {
    s3: null,
    polly: null,
    translate: null,
    transcribe: null
  };

  private languages = new Map([
    // bidirect languages
    [
      "zh-HK",
      {
        name: "Chinese (Traditional)",
        transcribeCode: "zh-HK",
        translationCode: "zh-TW",
        pollyCode: "yue-CN",
        voiceId: "Hiujin",
        engineType: "neural"
      }
    ],
    [
      "ar-AE",
      {
        name: "Arabic",
        transcribeCode: "ar-AE",
        translationCode: "ar",
        pollyCode: "ar-AE",
        voiceId: "Zayd",
        engineType: "neural"
      }
    ],
    [
      "fi-FI",
      {
        name: "Finnish",
        transcribeCode: "fi-FI",
        translationCode: "fi",
        pollyCode: "fi-FI",
        voiceId: "Suvi",
        engineType: "neural"
      }
    ],
    [
      "ca-ES",
      {
        name: "Catalan",
        transcribeCode: "ca-ES",
        translationCode: "ca",
        pollyCode: "ca-ES",
        voiceId: "Arlet",
        engineType: "neural"
      }
    ],
    [
      "ar-SA",
      {
        name: "Arabic",
        transcribeCode: "ar-SA",
        translationCode: "ar",
        pollyCode: "arb",
        voiceId: "Zeina",
        engineType: "standard"
      }
    ],
    [
      "en-US",
      {
        name: "English (US)",
        transcribeCode: "en-US",
        translationCode: "en",
        pollyCode: "en-US",
        voiceId: "Salli",
        engineType: "standard"
      }
    ],
    [
      "zh-CN",
      {
        name: "Chinese (Simplified)",
        transcribeCode: "zh-CN",
        translationCode: "zh",
        pollyCode: "cmn-CN",
        voiceId: "Zhiyu",
        engineType: "standard"
      }
    ],
    [
      "pl-PL",
      {
        name: "Polish",
        transcribeCode: "pl-PL",
        translationCode: "pl",
        pollyCode: "pl-PL",
        voiceId: "Ewa",
        engineType: "standard"
      }
    ],
    [
      "no-NO",
      {
        name: "Norwegian",
        transcribeCode: "no-NO",
        translationCode: "no",
        pollyCode: "nb-NO",
        voiceId: "Liv",
        engineType: "standard"
      }
    ],
    [
      "nl-NL",
      {
        name: "Dutch",
        transcribeCode: "nl-NL",
        translationCode: "nl",
        pollyCode: "nl-NL",
        voiceId: "Lotte",
        engineType: "standard"
      }
    ],
    [
      "pt-PT",
      {
        name: "Portuguese",
        transcribeCode: "pt-PT",
        translationCode: "pt-PT",
        pollyCode: "pt-PT",
        voiceId: "Ines",
        engineType: "standard"
      }
    ],
    [
      "de-DE",
      {
        name: "German",
        transcribeCode: "de-DE",
        translationCode: "de",
        pollyCode: "de-DE",
        voiceId: "Marlene",
        engineType: "standard"
      }
    ],
    [
      "it-IT",
      {
        name: "Italian",
        transcribeCode: "it-IT",
        translationCode: "it",
        pollyCode: "it-IT",
        voiceId: "Carla",
        engineType: "standard"
      }
    ],
    [
      "fr-FR",
      {
        name: "French",
        transcribeCode: "fr-FR",
        translationCode: "fr",
        pollyCode: "fr-FR",
        voiceId: "Celine",
        engineType: "standard"
      }
    ],
    [
      "ko-KR",
      {
        name: "Korean",
        transcribeCode: "ko-KR",
        translationCode: "ko",
        pollyCode: "ko-KR",
        voiceId: "Seoyeon",
        engineType: "standard"
      }
    ],
    [
      "hi-IN",
      {
        name: "Hindi",
        transcribeCode: "hi-IN",
        translationCode: "hi",
        pollyCode: "hi-IN",
        voiceId: "Aditi",
        engineType: "standard"
      }
    ],
    [
      "en-AU",
      {
        name: "English (Australian)",
        transcribeCode: "en-AU",
        translationCode: "en",
        pollyCode: "en-AU",
        voiceId: "Nicole",
        engineType: "standard"
      }
    ],
    [
      "pt-BR",
      {
        name: "Portuguese (Brazilian)",
        transcribeCode: "pt-BR",
        translationCode: "pt",
        pollyCode: "pt-BR",
        voiceId: "Camila",
        engineType: "standard"
      }
    ],
    [
      "sv-SE",
      {
        name: "Swedish",
        transcribeCode: "sv-SE",
        translationCode: "sv",
        pollyCode: "sv-SE",
        voiceId: "Astrid",
        engineType: "standard"
      }
    ],
    [
      "ja-JP",
      {
        name: "Japanese",
        transcribeCode: "ja-JP",
        translationCode: "ja",
        pollyCode: "ja-JP",
        voiceId: "Mizuki",
        engineType: "standard"
      }
    ],
    [
      "es-US",
      {
        name: "Spanish (Mexican)",
        transcribeCode: "es-US",
        translationCode: "es-MX",
        pollyCode: "es-MX",
        voiceId: "Mia",
        engineType: "standard"
      }
    ],
    [
      "fr-CA",
      {
        name: "French (Canadian)",
        transcribeCode: "fr-CA",
        translationCode: "fr-CA",
        pollyCode: "fr-CA",
        voiceId: "Chantal",
        engineType: "standard"
      }
    ],
    [
      "en-GB",
      {
        name: "English (British)",
        transcribeCode: "en-GB",
        translationCode: "en",
        pollyCode: "en-GB",
        voiceId: "Amy",
        engineType: "standard"
      }
    ],
    [
      "es-ES",
      {
        name: "Spanish",
        transcribeCode: "es-ES",
        translationCode: "es",
        pollyCode: "es-ES",
        voiceId: "Lucia",
        engineType: "standard"
      }
    ],

    // translate-to and polly languages
    [
      "en-NZ",
      {
        name: "English (New Zealand)",
        transcribeCode: "en-NZ",
        translationCode: "en",
        pollyCode: "en-NZ",
        voiceId: "Aria",
        engineType: "neural"
      }
    ],
    [
      "en-ZA",
      {
        name: "English (South African)",
        transcribeCode: "en-ZA",
        translationCode: "en",
        pollyCode: "en-ZA",
        voiceId: "Ayanda",
        engineType: "neural"
      }
    ],
    [
      "cy-WL",
      {
        name: "Welsh",
        transcribeCode: "cy-WL",
        translationCode: "cy",
        pollyCode: "cy-GB",
        voiceId: "Gwyneth",
        engineType: "standard"
      }
    ],
    [
      "da-DK",
      {
        name: "Danish",
        transcribeCode: "da-DK",
        translationCode: "da",
        pollyCode: "da-DK",
        voiceId: "Naja",
        engineType: "standard"
      }
    ],
    [
      "en-IN",
      {
        name: "English (Indian)",
        transcribeCode: "en-IN",
        translationCode: "en",
        pollyCode: "en-IN",
        voiceId: "Aditi",
        engineType: "standard"
      }
    ],
    [
      "en-WL",
      {
        name: "English (Welsh)",
        transcribeCode: "en-WL",
        translationCode: "en",
        pollyCode: "en-GB-WLS",
        voiceId: "Geraint",
        engineType: "standard"
      }
    ],
    [
      "is-IS",
      {
        name: "Icelandic",
        transcribeCode: "is-IS",
        translationCode: "is",
        pollyCode: "is-IS",
        voiceId: "Dora",
        engineType: "standard"
      }
    ],
    [
      "ro-RO",
      {
        name: "Romanian",
        transcribeCode: "ro-RO",
        translationCode: "ro",
        pollyCode: "ro-RO",
        voiceId: "Carmen",
        engineType: "standard"
      }
    ],
    [
      "ru-RU",
      {
        name: "Russian",
        transcribeCode: "ru-RU",
        translationCode: "ru",
        pollyCode: "ru-RU",
        voiceId: "Tatyana",
        engineType: "standard"
      }
    ],
    [
      "tr-TR",
      {
        name: "Turkish",
        transcribeCode: "tr-TR",
        translationCode: "tr",
        pollyCode: "tr-TR",
        voiceId: "Filiz",
        engineType: "standard"
      }
    ],

    // translate-to only languages
    [
      "af",
      {
        name: "Afrikaans",
        transcribeCode: "",
        translationCode: "af",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "sq",
      {
        name: "Albanian",
        transcribeCode: "",
        translationCode: "sq",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "am",
      {
        name: "Amharic",
        transcribeCode: "",
        translationCode: "am",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "hy",
      {
        name: "Armenian",
        transcribeCode: "",
        translationCode: "hy",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "az",
      {
        name: "Azerbaijani",
        transcribeCode: "",
        translationCode: "az",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "bn",
      {
        name: "Bengali",
        transcribeCode: "",
        translationCode: "bn",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "bs",
      {
        name: "Bosnian",
        transcribeCode: "",
        translationCode: "bs",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "bg",
      {
        name: "Bulgarian",
        transcribeCode: "",
        translationCode: "bg",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "hr",
      {
        name: "Croatian",
        transcribeCode: "",
        translationCode: "hr",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "cs",
      {
        name: "Czech",
        transcribeCode: "",
        translationCode: "cs",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "fa-AF",
      {
        name: "Dari",
        transcribeCode: "",
        translationCode: "fa-AF",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "et",
      {
        name: "Estonian",
        transcribeCode: "",
        translationCode: "et",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "fa",
      {
        name: "Farsi (Persian)",
        transcribeCode: "",
        translationCode: "fa",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "tl",
      {
        name: "Filipino Tagalog",
        transcribeCode: "",
        translationCode: "tl",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ka",
      {
        name: "Georgian",
        transcribeCode: "",
        translationCode: "ka",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "el",
      {
        name: "Greek",
        transcribeCode: "",
        translationCode: "el",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "gu",
      {
        name: "Gujarati",
        transcribeCode: "",
        translationCode: "gu",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ht",
      {
        name: "Haitian Creole",
        transcribeCode: "",
        translationCode: "ht",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ha",
      {
        name: "Hausa",
        transcribeCode: "",
        translationCode: "ha",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "he",
      {
        name: "Hebrew",
        transcribeCode: "",
        translationCode: "he",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "hu",
      {
        name: "Hungarian",
        transcribeCode: "",
        translationCode: "hu",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "id",
      {
        name: "Indonesian",
        transcribeCode: "",
        translationCode: "id",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ga",
      {
        name: "Irish",
        transcribeCode: "",
        translationCode: "ga",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "kk",
      {
        name: "Kazakh",
        transcribeCode: "",
        translationCode: "kk",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "lv",
      {
        name: "Latvian",
        transcribeCode: "",
        translationCode: "lv",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "lt",
      {
        name: "Lithuanian",
        transcribeCode: "",
        translationCode: "lt",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "mk",
      {
        name: "Macedonian",
        transcribeCode: "",
        translationCode: "mk",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ms",
      {
        name: "Malay",
        transcribeCode: "",
        translationCode: "ms",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ml",
      {
        name: "Malayalam",
        transcribeCode: "",
        translationCode: "ml",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "mt",
      {
        name: "Maltese",
        transcribeCode: "",
        translationCode: "mt",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "mr",
      {
        name: "Marathi",
        transcribeCode: "",
        translationCode: "mr",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "mn",
      {
        name: "Mongolian",
        transcribeCode: "",
        translationCode: "mn",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ps",
      {
        name: "Pashto",
        transcribeCode: "",
        translationCode: "ps",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "pa",
      {
        name: "Punjabi",
        transcribeCode: "",
        translationCode: "pa",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "sr",
      {
        name: "Serbian",
        transcribeCode: "",
        translationCode: "sr",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "si",
      {
        name: "Sinhala",
        transcribeCode: "",
        translationCode: "si",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "sk",
      {
        name: "Slovak",
        transcribeCode: "",
        translationCode: "sk",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "sl",
      {
        name: "Slovenian",
        transcribeCode: "",
        translationCode: "sl",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "so",
      {
        name: "Somali",
        transcribeCode: "",
        translationCode: "so",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "sw",
      {
        name: "Swahili",
        transcribeCode: "",
        translationCode: "sw",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ta",
      {
        name: "Tamil",
        transcribeCode: "",
        translationCode: "ta",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "te",
      {
        name: "Telugu",
        transcribeCode: "",
        translationCode: "te",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "th",
      {
        name: "Thai",
        transcribeCode: "",
        translationCode: "th",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "uk",
      {
        name: "Ukrainian",
        transcribeCode: "",
        translationCode: "uk",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "ur",
      {
        name: "Urdu",
        transcribeCode: "",
        translationCode: "ur",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "uz",
      {
        name: "Uzbek",
        transcribeCode: "",
        translationCode: "uz",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ],
    [
      "vi",
      {
        name: "Vietnamese",
        transcribeCode: "",
        translationCode: "vi",
        pollyCode: "",
        voiceId: "",
        engineType: ""
      }
    ]
  ]);

  private mediaStream: MediaStream | null = null;
  private transcription: { text: string; isPartial: boolean }[] = [];
  private stopTranscriptionResolve: Function | null = null;
  private stopTranscriptionForcedTimer: number | null = null;
  private microphoneStream: MicrophoneStream | null = null;
  private audioContext: AudioContext | null = null;

  private init(): void {
    this.checkConfig([
      "awsRegion",
      "awsAccessKeyId",
      "awsSecretAccessKey",
      "awsS3BucketName"
    ]);

    this.audioContext = new AudioContext();

    AWS.config.update({
      region: this.config.awsRegion,
      credentials: {
        accessKeyId: this.config.awsAccessKeyId!,
        secretAccessKey: this.config.awsSecretAccessKey!
      }
    });

    this.services.transcribe = new TranscribeStreamingClient({
      region: this.config.awsRegion!,
      credentials: {
        accessKeyId: this.config.awsAccessKeyId!,
        secretAccessKey: this.config.awsSecretAccessKey!
      }
    });

    this.services.polly = new AWS.Polly({
      apiVersion: "2016-06-10"
    });

    this.services.translate = new AWS.Translate({
      apiVersion: "2017-07-01"
    });

    this.services.s3 = new AWS.S3({
      apiVersion: "2006-03-01",
      params: { Bucket: this.config.awsS3BucketName! }
    });

    this.isInitialized = true;
  }

  private checkInitialization() {
    if (this.isInitialized) {
      return;
    }
    this.init();
  }

  private async *getAudioStream() {
    /* eslint-disable */
      // @ts-ignore
      for await (const chunk of this.microphoneStream) {
        /* eslint-enable */
      if (chunk.length <= this.audioContext!.sampleRate) {
        yield {
          AudioEvent: {
            AudioChunk: this.encodePcmChunk(chunk)
          }
        };
      }
    }
  }

  private encodePcmChunk(chunk: Buffer) {
    const input = MicrophoneStream.toRaw(chunk);
    let offset = 0;
    const buffer = new ArrayBuffer(input.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return Buffer.from(buffer);
  }

  public async startTranscription(
    stream: MediaStream,
    sourceLanguage: AwsLanguage,
    callback: Function
  ) {
    this.checkConfig(["awsRegion", "awsS3BucketName"]);

    this.checkInitialization();

    if (this.mediaStream || this.microphoneStream) {
      this.stopTranscription();
    }

    this.mediaStream = stream;
    this.microphoneStream = new MicrophoneStream();
    this.microphoneStream.setStream(this.mediaStream);

    const command = new StartStreamTranscriptionCommand({
      LanguageCode: this.languages.get(AwsLanguage[sourceLanguage])!
        .transcribeCode as LanguageCode,
      MediaEncoding: "pcm",
      MediaSampleRateHertz: this.audioContext!.sampleRate,
      AudioStream: this.getAudioStream(),
      EnablePartialResultsStabilization: false
    });

    const data = await this.services.transcribe.send(command);

    for await (const event of data.TranscriptResultStream) {
      const results = event.TranscriptEvent.Transcript.Results;

      if (results.length) {
        console.log(
          "Transcription chunk received: " +
            JSON.stringify({
              text: results[0].Alternatives[0].Transcript,
              isPartial: results[0].IsPartial
            })
        );

        this.transcription.push({
          text: results[0].Alternatives[0].Transcript,
          isPartial: results[0].IsPartial
        });
        callback(
          _.join(
            _.map(
              _.concat(
                _.filter(this.transcription, t => !t.isPartial),
                _.last(this.transcription)?.isPartial
                  ? [_.last(this.transcription)]
                  : []
              ),
              t => (t as { text: string; isPartial: boolean }).text
            ),
            " "
          )
        );

        if (
          this.stopTranscriptionResolve &&
          !_.last(this.transcription)?.isPartial
        ) {
          console.log("The sentence finished. Stopping transcription...");
          this.forceStopTranscription();
        }
      }
    }
  }
  private forceStopTranscription() {
    if (this.stopTranscriptionForcedTimer) {
      clearTimeout(this.stopTranscriptionForcedTimer);
      this.stopTranscriptionForcedTimer = null;
    }

    this.microphoneStream?.stop();
    /* eslint-disable */
    // @ts-ignore
    this.microphoneStream?.destroy();
    /* eslint-enable */
    this.microphoneStream = null;

    this.mediaStream?.getTracks()?.forEach?.(t => t.stop());
    this.mediaStream = null;

    this.stopTranscriptionResolve?.();
    this.stopTranscriptionResolve = null;

    this.transcription = [];
  }

  public stopTranscription(): Promise<void> {
    return new Promise<void>(resolve => {
      if (!this.microphoneStream) {
        resolve();
        return;
      }

      setTimeout(() => {
        if (_.last(this.transcription)?.isPartial === false) {
          // transcription sentence is finished
          this.forceStopTranscription();
          resolve();
          return;
        }
        console.log("The sentence is not finished. Waiting...");
        this.stopTranscriptionResolve = resolve;
        this.stopTranscriptionForcedTimer = window.setTimeout(() => {
          console.log("Stopping transcription by forced timer...");
          this.forceStopTranscription();
        }, 5000);
      }, 500);
    });
  }

  public async translate(
    text: string,
    sourceLanguage: AwsLanguage,
    targetLanguage: AwsLanguage
  ): Promise<{ audio?: string; text: string }> {
    this.checkInitialization();

    let resolve: Function, reject: Function;
    const promise = new Promise<{ audio?: string; text: string }>(
      (res, rej) => {
        resolve = res;
        reject = rej;
      }
    );

    const { translationCode, voiceId, engineType } = this.languages.get(
      AwsLanguage[targetLanguage]
    )!;

    this.services.translate!.translateText(
      {
        SourceLanguageCode: this.languages.get(AwsLanguage[sourceLanguage])!
          .translationCode,
        TargetLanguageCode: translationCode,
        Text: text,
        Settings: {
          // Brevity: ON,
          Formality: "FORMAL",
          Profanity: "MASK"
        }
      },
      (err: Error | null, data?: any) => {
        if (err) {
          reject(err);
          return;
        }
        const text = data.TranslatedText;

        if (!voiceId || !engineType) {
          resolve({ text });
          return;
        }

        const params = {
          Engine: engineType,
          OutputFormat: "mp3",
          Text: text,
          TextType: "text",
          VoiceId: voiceId
        };

        try {
          this.services.polly!.synthesizeSpeech(
            params,
            async (err: Error | null, data?: any) => {
              if (err) {
                reject(err);
                return;
              }
              const path = `output/${uuidv4()}.mp3`;
              await this.storeFile(data.AudioStream as Blob, path);
              resolve({ audio: path, text });
            }
          );
        } catch (err) {
          reject(err);
        }
      }
    );

    return promise;
  }

  private async storeFile(file: Blob, path: string) {
    this.checkInitialization();

    let resolve: Function, reject: Function;

    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    this.services.s3!.upload(
      {
        Key: path,
        Body: file
      },
      (err: Error | null, data?: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      }
    );

    return promise;
  }

  public async getFile(path: string): Promise<Uint8Array> {
    this.checkInitialization();

    let resolve: Function, reject: Function;

    const promise = new Promise<Uint8Array>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    this.services.s3!.getObject(
      { Key: path },
      (
        err: Error | null,
        data?: { Body: Uint8Array; ContentLength: number }
      ) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(data!.Body);
      }
    );

    return promise;
  }
}

export class StagingAwsService extends AbstractService
  implements AwsServiceInterface {
  public async getFile(path: string): Promise<Uint8Array> {
    this.checkConfig("stagingRequestDuration");

    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(new Uint8Array());
      }, this.config.stagingRequestDuration);
    });
  }

  public startTranscription(
    stream: MediaStream,
    sourceLanguage: AwsLanguage,
    callback: Function
  ): Promise<void> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
        callback("Staging transcription");
      }, this.config.stagingRequestDuration);
    });
  }

  public stopTranscription(): Promise<void> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, this.config.stagingRequestDuration);
    });
  }

  public translate(
    text: string,
    sourceLanguage: AwsLanguage,
    targetLanguage: AwsLanguage
  ): Promise<{ audio?: string; text: string }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          text: "Staging translation",
          audio: "Staging audio file path"
        });
      }, this.config.stagingRequestDuration);
    });
  }
}
